import * as quro from '@quromedical/fhir-common';
import {
  type CreateReference,
  createReference,
  GroupCode,
  GroupSecurity,
  getReferenceId,
  valsOrNone,
} from '@quromedical/fhir-common';
import { compact } from '@quromedical/utils';
import { getValidReference } from '../mappings/reference';
import type { WithId } from '../types';

import type {
  Group,
  GroupCreateRequest,
  GroupCreateResponse,
  GroupMember,
  GroupUpdateRequest,
} from './schema';

const securityCodes = Object.values(GroupSecurity);
const groupCodes = Object.values(GroupCode);

const getGroupCodes = (group: fhir4.Group): GroupSecurity[] => {
  const coding = group.code?.coding || [];
  const codes = coding.map((c) => c.code);

  const filteredCodes = compact(codes).filter((c) => {
    const hasCode = securityCodes.includes(c as GroupSecurity);
    return hasCode;
  }) as GroupSecurity[];

  return filteredCodes;
};

const getGroupType = (fhir: fhir4.Group): GroupCode | undefined => {
  const coding = fhir.code?.coding;

  if (!coding) {
    return undefined;
  }

  const foundCoding = coding.find(({ code }) => {
    if (!code) {
      return false;
    }

    return groupCodes.includes(code as GroupCode);
  });

  return foundCoding?.code as GroupCode | undefined;
};

const mapFhirToModelGroupMembers = (
  groupMember: fhir4.GroupMember[] = [],
): GroupMember[] => {
  const memberships = groupMember.map<GroupMember | undefined>(
    ({ entity, inactive }) => {
      const id = getReferenceId(entity);

      if (!id) {
        return undefined;
      }

      // if inactive is undefined, we assume the member is active
      return {
        id,
        active: !inactive,
        display: entity.display,
      };
    },
  );

  return compact(memberships);
};

const mapGroupToFhirMembers = (
  groupMembers: GroupMember[] = [],
): fhir4.GroupMember[] => {
  return groupMembers.map(({ id, active, display }) => ({
    entity: createReference('Practitioner', id, display),
    inactive: !active,
  }));
};

type GroupTypeCoding = quro.RequiredCoding<GroupSecurity | GroupCode>;
type GroupTypeCodeableConcept = fhir4.CodeableConcept & {
  coding: GroupTypeCoding[];
};

const createCoding = (code: GroupSecurity | GroupCode): GroupTypeCoding =>
  quro.createCoding(code);

const createGroupType = (
  type: GroupCode,
  permissions: GroupSecurity[],
): GroupTypeCodeableConcept => {
  const jointCodes = [...permissions, type];
  const coding = jointCodes.map(createCoding);
  return { coding };
};

export const mapGroupToFhir = (form: Group): fhir4.Group => {
  const { permissions, name, active, organization, type, members } = form;
  const code = createGroupType(type, permissions);
  const member = mapGroupToFhirMembers(members);

  return {
    name,
    resourceType: 'Group',
    actual: true,
    type: 'practitioner',
    active,
    code,
    managingEntity: organization,
    member,
  };
};

export const mapCreateModelToFhirGroup = (
  createModel: GroupCreateRequest,
): fhir4.Group => {
  return mapGroupToFhir({
    ...createModel,
    members: [],
    organization: createReference('Organization', createModel.organization),
  });
};

export const mapUpdateModelToFhirGroup = (
  fhir: WithId<fhir4.Group>,
  form: GroupUpdateRequest,
): WithId<fhir4.Group> => {
  const members = mapFhirToModelGroupMembers(fhir.member);

  const group = mapGroupToFhir({
    ...form,
    members,
    organization: createReference('Organization', form.organization),
  });

  return {
    ...group,
    id: fhir.id,
  };
};

export const mapFhirToModelGroup = (fhir: fhir4.Group): Partial<Group> => {
  const { name, active, managingEntity, member } = fhir;
  const codes = getGroupCodes(fhir);
  const organization = getValidReference(managingEntity);
  const type = getGroupType(fhir);
  const members = mapFhirToModelGroupMembers(member);

  return {
    name,
    active,
    permissions: codes,
    type,
    organization,
    members,
  };
};

export const mapFhirToModelGroupWithId = (
  fhir: WithId<fhir4.Group>,
): GroupCreateResponse => {
  const group = mapFhirToModelGroup(fhir);

  return {
    ...group,
    id: fhir.id,
  };
};

const createGroupMember = (
  active: boolean,
  entity: CreateReference,
): fhir4.GroupMember => ({
  inactive: !active,
  entity,
});

const isMemberInList = (
  member: fhir4.GroupMember,
  members: CreateReference[] = [],
): boolean => {
  const found = members.find((m) => m.reference !== member.entity.reference);

  return !!found;
};

export const updateGroupMembers = (
  group: WithId<fhir4.Group>,
  areMembersActive: boolean,
  membersToUpdate: CreateReference[],
): WithId<fhir4.Group> => {
  const groupMembers = group.member || [];

  const existingGroupMembers = groupMembers.filter((groupMember) =>
    isMemberInList(groupMember, membersToUpdate),
  );

  const updatedGroupMembers = membersToUpdate.map((ref) =>
    createGroupMember(areMembersActive, ref),
  );

  const mergedMembers = valsOrNone([
    ...updatedGroupMembers,
    ...existingGroupMembers,
  ]);

  return {
    ...group,
    member: mergedMembers,
  };
};
